import type { MissionsGateway, RepTargets } from './types';
import type { Mission } from '../../api/missions-types';
import { missionLeadsListSchema } from './schemas';
import { mapUiStatusToDb } from '../../status/missionStatus';
import { serviceFetch } from '../../http/serviceLayerClient';
import { cachedFetch, getCacheKey } from '../../http/requestCache';
import { ContractMismatchError } from '../../http/slErrors';
import {
  DEFAULT_REP_TARGETS,
  buildCtx,
  extractMissionListItems,
  toDbMissionPayload,
  toUiMission,
  toUiMissionLead,
  unwrapEnvelope,
  type RepTargetsResponse,
} from './serviceLayerGateway.mappers';
// Per-row resilience helpers extracted to keep this file ≤300 LOC. Re-exported
// so existing consumers (and tests) can keep importing from this module.
import { normalizeMissionArray, normalizeMissionLeadArray } from './serviceLayerGateway.normalize';

export { normalizeMissionArray, normalizeMissionLeadArray };

export const serviceLayerGateway: MissionsGateway = {
  async list(email?: string, role: 'Admin' | 'Manager' | 'Sales' | 'Viewer' = 'Viewer') {
    const ctx = await buildCtx();
    // Fetch ALL missions from the API (no server-side owner_email filter —
    // the Service Layer may not support it, and we need reliable client-side filtering).
    // Server applies a page size when no `limit` is sent (prod observed: "API
    // returned 100 raw items", silently dropping the all-reps pool's tail).
    // GET /v1/missions documents limit (default 50, max 200 — openapi.json);
    // request that documented max.
    const MISSIONS_LIST_LIMIT = 200;
    const url = `/v1/missions?limit=${MISSIONS_LIST_LIMIT}`;
    const cacheKey = getCacheKey(url);

    // Cache GET requests for 2 minutes
    const result = await cachedFetch(
      url,
      async () => {
        const response = await serviceFetch(url, { ctx });
        const items = extractMissionListItems(response.data);

        const rows: Mission[] = normalizeMissionArray(items);
        const atCeiling = items.length >= MISSIONS_LIST_LIMIT;
        const log = atCeiling ? console.error : console.warn;
        log(`[MissionsGateway] list() — API returned ${items.length} raw items, parsed ${rows.length} missions. role=${role}, email=${email}${atCeiling ? ` — AT/OVER limit=${MISSIONS_LIST_LIMIT}, pool may be truncated again` : ''}`);
        if (rows.length > 0) {
          console.warn('[MissionsGateway] owner_emails in response:', rows.map(m => m.ownerEmail));
        }
        return rows;
      },
      { ttlMs: 2 * 60 * 1000, cacheKey, swr: true } // 2 minute cache with SWR
    );

    // Client-side filtering by API role (already resolved from permissions by caller):
    // 'Sales' (own scope): show only own missions (by ownerEmail)
    // 'Admin'/'Viewer': show all missions
    if (role === 'Sales' && email) {
      const filtered = result.filter(m => m.ownerEmail?.toLowerCase() === email.toLowerCase());
      console.warn(`[MissionsGateway] Own-scope filter: ${result.length} → ${filtered.length} missions for ${email}`);
      return filtered;
    }
    return result;
  },
  async get(id: string) {
    const ctx = await buildCtx();
    const url = `/v1/missions/${id}`;
    const cacheKey = getCacheKey(url);

    // Cache GET requests for 1 minute
    const result = await cachedFetch(
      url,
      async () => {
        const response = await serviceFetch<any>(url, { ctx });
        // Backend returns { success: true, data: mission }, so we need response.data.data
        const missionData = response.data?.data;
        if (!missionData) {
          console.error('[MissionsGateway] getMission returned no data for mission:', id);
          throw new Error(`Mission ${id} not found`);
        }
        return toUiMission(missionData);
      },
      { ttlMs: 60 * 1000, cacheKey } // 1 minute cache
    );

    return result;
  },
  async create(newMission: Omit<Mission, 'id'>) {
    const ctx = await buildCtx();
    const payload = toDbMissionPayload(newMission);
    // Generate idempotency key for reliable mission creation
    const idempotencyKey = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const headers: Record<string, string> = {
      'X-Idempotency-Key': idempotencyKey,
    };
    const { data: raw } = await serviceFetch(`/v1/missions`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers,
      ctx
    });
    // Invalidate missions list cache
    const { requestCache } = await import('../../http/requestCache');
    requestCache.invalidatePattern(/^GET:\/v1\/missions/);
    return toUiMission(unwrapEnvelope(raw));
  },
  async update(id: string, updates: Partial<Mission>) {
    const ctx = await buildCtx();
    const payload: any = {};
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.date !== undefined) {
      payload.assigned_date = updates.date || null;
      if (updates.date) {
        const dayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
        const d = new Date(updates.date + 'T00:00:00');
        if (!isNaN(d.getTime())) payload.assigned_day = dayNames[d.getDay()] || null;
      } else {
        payload.assigned_day = null;
      }
    }
    if (updates.region !== undefined) payload.region_id = updates.region;
    if (updates.ownerEmail !== undefined) payload.owner_email = updates.ownerEmail;
    if (updates.status !== undefined) payload.status = mapUiStatusToDb(updates.status);
    const { data: raw } = await serviceFetch(`/v1/missions/${id}`, { method: 'PATCH', body: JSON.stringify(payload), ctx });
    // Invalidate cache for this mission and missions list
    const { requestCache } = await import('../../http/requestCache');
    requestCache.invalidate(getCacheKey(`/v1/missions/${id}`));
    requestCache.invalidatePattern(/^GET:\/v1\/missions/);
    // D-33 R1 — PATCH landed server-side; if the response row fails to
    // normalize we must NOT swallow it into a soft-success nor let an opaque
    // Zod throw bubble as if the write failed. Re-throw a typed mismatch.
    try {
      return toUiMission(unwrapEnvelope(raw));
    } catch (err) {
      throw new ContractMismatchError({
        slug: 'missions.update', expectedShape: 'Mission', actualShape: typeof raw, rawData: raw,
        message: `Mission ${id} update persisted but response failed to normalize: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  },
  async delete(id: string) {
    const ctx = await buildCtx();
    await serviceFetch(`/v1/missions/${id}`, { method: 'DELETE', ctx });
    // Invalidate cache
    const { requestCache } = await import('../../http/requestCache');
    requestCache.invalidate(getCacheKey(`/v1/missions/${id}`));
    requestCache.invalidatePattern(/^GET:\/v1\/missions/);
  },
  async archive(id: string) {
    // Service layer does not persist archive flag in SP-014; emulate via update if needed
    const m = await this.get(id);
    if (!m) {
      throw new Error(`Mission ${id} not found`);
    }
    const mission: Mission = m;
    return { ...mission, archived: true };
  },
  async unarchive(id: string) {
    const m = await this.get(id);
    if (!m) {
      throw new Error(`Mission ${id} not found`);
    }
    const mission: Mission = m;
    return { ...mission, archived: false };
  },
  async rate(id: string, rating: { decisionMaker: boolean; demoPerformed: boolean; wonAmountEur: number }) {
    const m = await this.get(id);
    return { ...m, ratingDecisionMaker: rating.decisionMaker, ratingDemoPerformed: rating.demoPerformed, ratingWonAmountEur: rating.wonAmountEur } as Mission;
  },
  async listLeads(missionId: string, opts?: { signal?: AbortSignal }) {
    const url = `/v1/missions/${missionId}/stops`;

    return cachedFetch(
      url,
      async () => {
        const ctx = await buildCtx();
        let raw: unknown;
        try {
          const res = await serviceFetch(url, { ctx, timeoutMs: 10000, signal: opts?.signal });
          raw = res.data;
        } catch (e: unknown) {
          // 404 = no stops for this mission — return empty array
          if (e && typeof e === 'object' && 'status' in e && (e as { status: number }).status === 404) {
            return [];
          }
          throw e;
        }

        let items: any[];
        const envelope = raw as any;
        if (envelope && typeof envelope === 'object' && 'data' in envelope && Array.isArray(envelope.data)) {
          items = envelope.data;
        } else if (Array.isArray(raw)) {
          items = raw;
        } else {
          items = [];
        }

        const parsed = missionLeadsListSchema.safeParse(items);
        items = parsed.success ? parsed.data : items;
        return normalizeMissionLeadArray(items);
      },
      { ttlMs: 30 * 1000, cacheKey: getCacheKey(url) }
    );
  },
  async assignLeads(missionId: string, leadIds: string[]) {
    const ctx = await buildCtx();
    const { data: raw } = await serviceFetch(`/v1/missions/${missionId}/leads`, {
      method: 'POST',
      body: JSON.stringify({ lead_ids: leadIds }),
      ctx,
    });
    // Invalidate leads cache for this mission
    const { requestCache } = await import('../../http/requestCache');
    requestCache.invalidate(getCacheKey(`/v1/missions/${missionId}/stops`));
    const unwrapped = unwrapEnvelope(raw);
    const arr = Array.isArray(unwrapped) ? unwrapped : [];
    const parsed = missionLeadsListSchema.safeParse(arr);
    const items = parsed.success ? parsed.data : arr;
    return normalizeMissionLeadArray(items);
  },
  async reorderLead(missionId: string, missionLeadId: string, newIndex: number) {
    const ctx = await buildCtx();
    const { data: raw } = await serviceFetch(`/v1/missions/${missionId}/stops/reorder`, { method: 'POST', body: JSON.stringify({ mission_lead_id: missionLeadId, new_index: newIndex }), ctx });
    // Invalidate leads cache for this mission
    const { requestCache } = await import('../../http/requestCache');
    requestCache.invalidate(getCacheKey(`/v1/missions/${missionId}/stops`));
    const unwrapped = unwrapEnvelope(raw);
    const arr = Array.isArray(unwrapped) ? unwrapped : [];
    const parsed = missionLeadsListSchema.safeParse(arr);
    const items = parsed.success ? parsed.data : arr;
    return normalizeMissionLeadArray(items);
  },
  async deleteLead(missionLeadId: string) {
    const ctx = await buildCtx();
    await serviceFetch(`/v1/missions/stops/${missionLeadId}`, { method: 'DELETE', ctx });
    // Invalidate all mission leads caches (we don't know which mission this lead belongs to)
    const { requestCache } = await import('../../http/requestCache');
    requestCache.invalidatePattern(/^GET:\/v1\/missions\/.*\/stops/);
  },
  async updateLead(missionLeadId: string, updates: { plannedTime?: string; status?: string; pinned?: boolean; is_anchor?: boolean; anchor_source?: string }, missionId?: string) {
    const ctx = await buildCtx();
    const payload: any = {};
    if (updates.plannedTime !== undefined) payload.planned_time = updates.plannedTime;
    if (updates.status !== undefined) payload.status = updates.status;
    if (updates.pinned !== undefined) { payload.is_anchor = updates.pinned; payload.anchor_source = 'manual'; }
    if (updates.is_anchor !== undefined) payload.is_anchor = updates.is_anchor;
    if (updates.anchor_source !== undefined) payload.anchor_source = updates.anchor_source;
    const url = missionId
      ? `/v1/missions/${missionId}/stops/${missionLeadId}`
      : `/v1/missions/stops/${missionLeadId}`;
    const { data: raw } = await serviceFetch(url, { method: 'PATCH', body: JSON.stringify(payload), ctx });
    // Invalidate all mission leads caches (we don't know which mission this lead belongs to)
    const { requestCache } = await import('../../http/requestCache');
    requestCache.invalidatePattern(/^GET:\/v1\/missions\/.*\/stops/);
    return toUiMissionLead(unwrapEnvelope(raw));
  },
  async getRepTargets(): Promise<RepTargets> {
    try {
      const ctx = await buildCtx();
      const { data } = await serviceFetch<RepTargetsResponse | { data: RepTargetsResponse }>(
        '/v1/rep-targets',
        {
          ctx,
          timeoutMs: 10000,
        },
      );

      const envelope = data as any;
      const raw: RepTargetsResponse =
        envelope && typeof envelope === 'object' && 'data' in envelope
          ? (envelope.data as RepTargetsResponse)
          : (envelope as RepTargetsResponse);

      return {
        visitsPerDay: raw?.visits_per_day ?? DEFAULT_REP_TARGETS.visitsPerDay,
        demosPerDay: raw?.demos_per_day ?? DEFAULT_REP_TARGETS.demosPerDay,
        ordersPerDay: raw?.orders_per_day ?? DEFAULT_REP_TARGETS.ordersPerDay,
        revenuePerDayEur: raw?.revenue_per_day_eur ?? DEFAULT_REP_TARGETS.revenuePerDayEur,
        demoRateThresholdPct: raw?.demo_rate_threshold_pct ?? DEFAULT_REP_TARGETS.demoRateThresholdPct,
      };
    } catch {
      return DEFAULT_REP_TARGETS;
    }
  },
};
